import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trash2, MapPin, Pencil, Navigation, ChevronDown, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateSeeding } from '@/hooks/use-mutations'
import { useTinyStatus, useTinyShippingMethods } from '@/hooks/use-tiny'
import { TinyProductSearch } from './tiny-product-search'
import { AddressFormFields } from '@/components/address-form-fields'
import type { TinyProduto } from '@/lib/types'

const seedingFormSchema = z.object({
  products: z
    .array(
      z.object({
        sku: z.string().min(1, 'SKU obrigatório'),
        name: z.string().min(1, 'Nome do produto obrigatório'),
        quantity: z.coerce.number().min(1, 'Mínimo 1').max(100, 'Máximo 100'),
        unitCost: z.coerce.number().min(0, 'Custo não pode ser negativo'),
        tinyProductId: z.string().optional(),
      })
    )
    .min(1, 'Adicione pelo menos um produto'),
  shippingAddress: z
    .object({
      street: z.string().min(1, 'Obrigatório'),
      number: z.string().min(1, 'Obrigatório'),
      complement: z.string().optional(),
      neighborhood: z.string().min(1, 'Obrigatório'),
      city: z.string().min(1, 'Obrigatório'),
      state: z.string().min(2, 'Obrigatório').max(2, 'Apenas UF'),
      zipCode: z.string().min(8, 'CEP Inválido'),
    })
    .optional(),
  shippingMethod: z.string().optional(),
  financeCategory: z.string().optional(),
  notes: z.string().optional(),
})

type SeedingFormValues = z.infer<typeof seedingFormSchema>

interface CreatorAddress {
  street?: string
  number?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  zipCode?: string
}

interface SeedingFormDialogProps {
  creatorId: string
  creatorAddress?: CreatorAddress | null
  creatorCpf?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const FALLBACK_SHIPPING_METHODS = [
  { code: 'C', name: 'Correios' },
  { code: 'T', name: 'Transportadora' },
  { code: 'X', name: 'Customizada' },
  { code: 'J', name: 'Jadlog' },
  { code: 'S', name: 'Sem Frete' },
  { code: 'LOGGI', name: 'Loggi' },
]

export function SeedingFormDialog({
  creatorId,
  creatorAddress,
  creatorCpf,
  open,
  onOpenChange,
}: SeedingFormDialogProps) {
  const createSeeding = useCreateSeeding()
  const { data: tinyStatus } = useTinyStatus()
  const tinyConnected = tinyStatus?.enabled ?? false
  const { data: shippingMethods } = useTinyShippingMethods(tinyConnected)
  const missingCpf = !creatorCpf

  const envioOptions = shippingMethods?.length
    ? shippingMethods.map((m) => ({ code: m.id.toString(), name: m.nome }))
    : FALLBACK_SHIPPING_METHODS

  const hasAddress = !!(creatorAddress?.street && creatorAddress?.zipCode)
  const [addressMode, setAddressMode] = useState<'use_existing' | 'edit' | 'new'>(
    hasAddress ? 'use_existing' : 'new'
  )
  const [showManualAdd, setShowManualAdd] = useState(false)

  const getAddressDefaults = (mode: 'use_existing' | 'edit' | 'new') => {
    if (mode === 'edit') {
      return {
        street: creatorAddress?.street || '',
        number: creatorAddress?.number || '',
        complement: creatorAddress?.complement || '',
        neighborhood: creatorAddress?.neighborhood || '',
        city: creatorAddress?.city || '',
        state: creatorAddress?.state || '',
        zipCode: creatorAddress?.zipCode || '',
      }
    }
    if (mode === 'new' && !hasAddress) {
      return {
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
      }
    }
    return undefined
  }

  const form = useForm<SeedingFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(seedingFormSchema) as any,
    defaultValues: {
      products: [],
      shippingAddress: getAddressDefaults(addressMode),
      shippingMethod: tinyStatus?.shippingMethod || '',
      financeCategory: tinyStatus?.financeCategory || '',
      notes: '',
    },
  })

  const { fields, append, remove } = useFieldArray({
    name: 'products',
    control: form.control,
  })

  function handleProductSelect(product: TinyProduto) {
    // Don't add duplicates
    const existing = form.getValues('products')
    if (existing.some((p) => p.sku === product.codigo)) return

    append({
      sku: product.codigo,
      name: product.nome,
      quantity: 1,
      unitCost: product.preco,
      tinyProductId: product.id,
    })
  }

  function handleAddressMode(mode: 'use_existing' | 'edit' | 'new') {
    setAddressMode(mode)
    if (mode === 'use_existing') {
      form.setValue('shippingAddress', undefined)
    } else {
      form.setValue('shippingAddress', getAddressDefaults(mode)!)
    }
  }

  function onSubmit(data: SeedingFormValues) {
    createSeeding.mutate({ creatorId, ...data } as SeedingFormValues & { creatorId: string }, {
      onSuccess: () => {
        form.reset()
        setAddressMode(hasAddress ? 'use_existing' : 'new')
        setShowManualAdd(false)
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Novo Envio de Produto</SheetTitle>
          <SheetDescription>
            {tinyConnected
              ? 'Busque produtos do Tiny e configure o envio de bonificação.'
              : 'Registre um novo envio (seeding) de bonificação.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-6">
              {/* Products Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Produtos</h4>
                </div>

                {/* Tiny Product Search */}
                {tinyConnected && <TinyProductSearch onSelect={handleProductSelect} />}

                {/* Product List */}
                {fields.length > 0 && (
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="flex gap-3 items-center p-3 border rounded-md bg-muted/40"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{field.name}</div>
                          <Badge variant="secondary" className="text-xs font-mono mt-1">
                            {field.sku}
                          </Badge>
                        </div>
                        <FormField
                          control={form.control}
                          name={`products.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem className="w-20">
                              <FormLabel className="text-xs sr-only">Qtd</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  className="text-center"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`products.${index}.unitCost`}
                          render={({ field }) => (
                            <FormItem className="w-28">
                              <FormLabel className="text-xs sr-only">Valor</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                    R$
                                  </span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="pl-7 text-right"
                                    {...field}
                                  />
                                </div>
                              </FormControl>
                              {tinyStatus?.itemPriceStrategy === 'percentage' &&
                                tinyStatus.itemPricePercent && (
                                  <p className="text-[10px] text-muted-foreground text-right">
                                    Nota: R${' '}
                                    {(
                                      Math.round(
                                        Number(field.value) * tinyStatus.itemPricePercent
                                      ) / 100
                                    ).toFixed(2)}
                                  </p>
                                )}
                              {tinyStatus?.itemPriceStrategy === 'zero' && (
                                <p className="text-[10px] text-muted-foreground text-right">
                                  Nota: R$ 0,00
                                </p>
                              )}
                            </FormItem>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive shrink-0"
                          onClick={() => remove(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {fields.length === 0 && !tinyConnected && (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-md border-dashed">
                    Adicione produtos manualmente abaixo.
                  </p>
                )}

                {fields.length === 0 && tinyConnected && (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-md border-dashed">
                    Busque e selecione produtos do Tiny acima.
                  </p>
                )}

                {/* Manual Add Fallback */}
                {tinyConnected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => setShowManualAdd(!showManualAdd)}
                  >
                    <ChevronDown
                      className={`mr-1 h-3 w-3 transition-transform ${showManualAdd ? 'rotate-180' : ''}`}
                    />
                    Adicionar manualmente
                  </Button>
                )}

                {(showManualAdd || !tinyConnected) && (
                  <ManualProductAdd onAdd={(product) => append(product)} />
                )}

                {form.formState.errors.products?.root && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.products.root.message}
                  </p>
                )}
              </div>

              {/* Address Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium border-b pb-2">Endereço de Destino</h4>

                {hasAddress ? (
                  <div className="space-y-3">
                    {addressMode === 'use_existing' && (
                      <div className="p-3 border rounded-md bg-muted/40 space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="text-sm">
                            <p>
                              {creatorAddress?.street}, {creatorAddress?.number}
                              {creatorAddress?.complement ? ` - ${creatorAddress.complement}` : ''}
                            </p>
                            <p className="text-muted-foreground">
                              {creatorAddress?.neighborhood} - {creatorAddress?.city}/
                              {creatorAddress?.state}
                            </p>
                            <p className="text-muted-foreground">CEP: {creatorAddress?.zipCode}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddressMode('edit')}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            Editar
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddressMode('new')}
                          >
                            <Navigation className="mr-1 h-3 w-3" />
                            Enviar para outro
                          </Button>
                        </div>
                      </div>
                    )}

                    {addressMode !== 'use_existing' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {addressMode === 'edit'
                              ? 'Editando endereço do creator'
                              : 'Endereço alternativo'}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                            onClick={() => handleAddressMode('use_existing')}
                          >
                            Usar endereço cadastrado
                          </Button>
                        </div>
                        <AddressFormFields form={form} prefix="shippingAddress" />
                      </div>
                    )}
                  </div>
                ) : (
                  <AddressFormFields form={form} prefix="shippingAddress" />
                )}
              </div>

              {/* Shipping Method */}
              <FormField
                control={form.control}
                name="shippingMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Método de Envio</FormLabel>
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione (opcional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {envioOptions.map((m) => (
                          <SelectItem key={m.code} value={m.code}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Finance Category — only shown when Tiny is connected and categories are configured */}
              {tinyConnected &&
                tinyStatus?.financeCategories &&
                tinyStatus.financeCategories.length > 0 && (
                  <FormField
                    control={form.control}
                    name="financeCategory"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoria Financeira (opcional)</FormLabel>
                        <Select
                          value={field.value || '__none__'}
                          onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione (opcional)" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">Nenhuma</SelectItem>
                            {tinyStatus.financeCategories.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                                {cat === tinyStatus.financeCategory && (
                                  <span className="ml-1.5 text-muted-foreground text-xs">
                                    (padrão)
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observações Internas (Opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Informações adicionais da nota, brindes ou instruções..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {missingCpf && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Creator sem CPF/CNPJ cadastrado. O pedido pode falhar no Tiny. Cadastre o CPF na
                    aba de configurações do creator antes de enviar.
                  </AlertDescription>
                </Alert>
              )}
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" disabled={missingCpf} loading={createSeeding.isPending}>
                Programar Envio
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// Sub-components

function ManualProductAdd({
  onAdd,
}: {
  onAdd: (product: { sku: string; name: string; quantity: number; unitCost: number }) => void
}) {
  const [sku, setSku] = useState('')
  const [name, setName] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [unitCost, setUnitCost] = useState(0)

  const handleAdd = () => {
    if (!sku.trim() || !name.trim()) return
    onAdd({ sku: sku.trim(), name: name.trim(), quantity, unitCost })
    setSku('')
    setName('')
    setQuantity(1)
    setUnitCost(0)
  }

  return (
    <div className="flex gap-2 items-end p-3 border rounded-md border-dashed bg-muted/20">
      <div className="flex-1 max-w-[100px]">
        <label className="text-xs font-medium">SKU</label>
        <Input
          placeholder="NTR-001"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          className="mt-1"
        />
      </div>
      <div className="flex-[2]">
        <label className="text-xs font-medium">Nome</label>
        <Input
          placeholder="Produto..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
        />
      </div>
      <div className="w-16">
        <label className="text-xs font-medium">Qtd</label>
        <Input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          className="mt-1"
        />
      </div>
      <div className="w-24">
        <label className="text-xs font-medium">Custo</label>
        <Input
          type="number"
          min={0}
          step={0.01}
          value={unitCost}
          onChange={(e) => setUnitCost(Number(e.target.value))}
          className="mt-1"
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
        disabled={!sku.trim() || !name.trim()}
        className="shrink-0"
      >
        Adicionar
      </Button>
    </div>
  )
}
